import React from 'react';
import clsx from 'clsx';
import {
  ArrowUturnLeftIcon,
  ArrowUturnRightIcon,
  ChevronRightIcon
} from '@heroicons/react/24/outline';
import { Button } from '@oysterjs/ui/button';
import {
  BrandPalette,
  BrandColorScheme,
  BrandState,
  paletteOf,
  ELEMENTS,
  ElementStyle,
  elementOn,
  PALETTE_ROLES,
  shows,
  stripeFillOf,
  typeScaleWarnings
} from './design-brand';
import {
  ElementFields,
  IconAction,
  LogoFields,
  MarginFields,
  SlotFields,
  TypefaceFields,
  TypefaceOption,
  VoiceFields
} from './design-fields';
import { ColorRow, ColorSchemeTile, matchingColorScheme } from './design-palette';
import { Region } from './design-preview';

export const BrandControls = ({
  tab,
  onTab,
  onFocusElement,
  draft,
  voiceEditable = true,
  typefaces,
  colorSchemes,
  defaults,
  error,
  onSaveAsNew,
  saveHeld,
  canUndo,
  canRedo,
  onUndo,
  onRedo,
  onChange,
  head,
  pageSection
}: {
  tab: Tab;
  onTab: (tab: Tab) => void;
  onFocusElement: (element: ElementStyle['element']) => void;
  draft: BrandState;
  voiceEditable?: boolean;
  typefaces: TypefaceOption[];
  colorSchemes: BrandColorScheme[];
  defaults: ElementStyle[];
  error?: string | null;
  /** Offered beside the error, for a draft its design can no longer take. */
  onSaveAsNew?: () => void;
  /** Why Save is held, said above the tabs for a tab that does not say it itself. */
  saveHeld?: string | null;
  canUndo: boolean;
  canRedo: boolean;
  onUndo: () => void;
  onRedo: () => void;
  onChange: (next: BrandState) => void;
  /** Fields of the design rather than its brand, above the tabs. */
  head?: React.ReactNode;
  /** Shown on the Page tab beneath the margins. */
  pageSection?: React.ReactNode;
}) => {
  // One color open at a time: five score cards at once is a wall nobody reads.
  const [open, setOpen] = React.useState<keyof BrandPalette | null>(null);
  const next = TABS[TABS.indexOf(tab) + 1];
  // On every tab that sets a size, because the cause can be a size set on another tab.
  const typeScale = tab.elements.some((element) => shows(element, 'size'))
    ? typeScaleWarnings(draft, defaults)
    : [];

  return (
    <div className="flex min-h-0 max-h-full flex-col rounded-lg bg-white shadow dark:bg-neutral-800">
      {(head || error || saveHeld) && (
        <div className="shrink-0 border-b border-neutral-100 px-4 pb-3 pt-2 dark:border-neutral-700">
          {head}
          {error && (
            <p role="alert" className="mt-2 text-xs text-red-600 dark:text-red-400">
              There was an error saving: {error}
              {onSaveAsNew && (
                <>
                  {' '}
                  <button
                    type="button"
                    onClick={onSaveAsNew}
                    className="font-medium text-sky-600 hover:underline dark:text-sky-400"
                  >
                    Save as a new design
                  </button>
                </>
              )}
            </p>
          )}
          {saveHeld && (
            <p role="alert" className="mt-2 text-xs text-red-600 dark:text-red-400">
              {saveHeld}
            </p>
          )}
        </div>
      )}
      {/* Wraps rather than scrolls: nine tabs never fit the panel's width, and a scrolling strip
          hides the last of them with no sign that it scrolls. */}
      <div
        className="flex shrink-0 flex-wrap gap-1 border-b border-neutral-100 p-2 dark:border-neutral-700"
        role="tablist"
      >
        {TABS.map((each) => (
          <button
            key={each.key}
            type="button"
            role="tab"
            aria-selected={each.key === tab.key}
            onClick={() => onTab(each)}
            className={clsx(
              'rounded-md px-2 py-1 text-xs font-medium transition',
              each.key === tab.key
                ? 'bg-sky-50 text-sky-700 dark:bg-sky-500/10 dark:text-sky-300'
                : 'text-neutral-500 hover:bg-neutral-50 hover:text-neutral-800 dark:text-neutral-400 dark:hover:bg-white/5 dark:hover:text-neutral-100'
            )}
          >
            {each.label}
          </button>
        ))}
      </div>

      <div className="flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto p-4">
        {tab.roles.length > 0 && (
          <section>
            <div className="flex items-center justify-between">
              <h2 className="text-sm font-medium text-neutral-900 dark:text-white">Palette</h2>
              <div className="flex items-center gap-0.5">
                <IconAction label="Undo" disabled={!canUndo} onClick={onUndo}>
                  <ArrowUturnLeftIcon className="size-4" />
                </IconAction>
                <IconAction label="Redo" disabled={!canRedo} onClick={onRedo}>
                  <ArrowUturnRightIcon className="size-4" />
                </IconAction>
              </div>
            </div>
            <div className="mt-2 grid grid-cols-3 gap-2">
              {colorSchemes.map((scheme) => (
                <ColorSchemeTile
                  key={scheme.key}
                  scheme={scheme}
                  active={matchingColorScheme(colorSchemes, draft.palette)?.key === scheme.key}
                  onPick={() => onChange({ ...draft, palette: paletteOf(scheme.palette) })}
                />
              ))}
            </div>
          </section>
        )}

        {tab.roles.length > 0 && (
          <section>
            <h2 className="text-sm font-medium text-neutral-900 dark:text-white">Colors</h2>
            <div className="mt-2 flex flex-col gap-1">
              {PALETTE_ROLES.filter((role) => tab.roles.includes(role.key)).map((role) => (
                <ColorRow
                  key={role.key}
                  role={role}
                  palette={draft.palette}
                  stripeFill={stripeFillOf(draft.elements)}
                  open={open === role.key}
                  onToggle={() => setOpen(open === role.key ? null : role.key)}
                  onChange={(palette) => onChange({ ...draft, palette })}
                />
              ))}
            </div>
          </section>
        )}

        {tab.logos && (
          <LogoFields logos={draft.logos} onChange={(logos) => onChange({ ...draft, logos })} />
        )}

        {tab.voice && (
          <VoiceFields
            voice={draft.voice}
            editable={voiceEditable}
            onChange={(voice) => onChange({ ...draft, voice })}
          />
        )}

        {tab.margins && (
          <MarginFields
            margins={draft.margins}
            onChange={(margins) => onChange({ ...draft, margins })}
          />
        )}

        {tab.margins && pageSection}

        {tab.slots && (
          <SlotFields
            which={tab.slots}
            slots={tab.slots === 'header' ? draft.header : draft.footer}
            logos={draft.logos}
            onChange={(slots) =>
              onChange(
                tab.slots === 'header' ? { ...draft, header: slots } : { ...draft, footer: slots }
              )
            }
          />
        )}

        {typeScale.length > 0 && (
          <section role="status" aria-label="Font sizes">
            {typeScale.map((warning) => (
              <p key={warning} className="text-xs text-amber-700 dark:text-amber-400">
                {warning}
              </p>
            ))}
          </section>
        )}

        {tab.elements.map((element) => (
          <ElementFields
            key={element}
            element={element}
            elements={draft.elements}
            defaults={defaults}
            typefaces={typefaces}
            palette={draft.palette}
            headingTypeface={draft.headingTypeface}
            bodyTypeface={draft.bodyTypeface}
            disabled={!elementOn(element, draft)}
            onFocus={() => onFocusElement(element)}
            onChange={(elements) => onChange({ ...draft, elements })}
          />
        ))}

        {/* Brand-wide, so it belongs with the palette rather than repeated on every tab: the
            element tabs each carry their own family, which follows these two by default. */}
        {tab.roles.length > 0 && (
          <TypefaceFields
            typefaces={typefaces}
            bodyTypeface={draft.bodyTypeface}
            headingTypeface={draft.headingTypeface}
            onChange={(next) => onChange({ ...draft, ...next })}
          />
        )}
      </div>

      {next && (
        <div className="flex shrink-0 justify-end border-t border-neutral-100 p-4 dark:border-neutral-700">
          <Button outline onClick={() => onTab(next)}>
            {next.label}
            <ChevronRightIcon />
          </Button>
        </div>
      )}
    </div>
  );
};

export type Tab = {
  key: string;
  label: string;
  /** What the preview zooms to when this tab opens. */
  region: Region;
  /** The palette rows this tab shows. Only the first tab is about color as such. */
  roles: (keyof BrandPalette)[];
  /** The elements this tab sets the type of. */
  elements: ElementStyle['element'][];
  /** Page setup rather than type: the margin the sheet is printed with. */
  margins?: boolean;
  /** Which running slots this tab writes, when it writes any. */
  slots?: 'header' | 'footer';
  /** The agency's marks, by the background each is for. */
  logos?: boolean;
  /** How the agency sounds; words for the model rather than values for the renderer. */
  voice?: boolean;
};

/** Ordered, because Next walks them: the page, then what sits on it, then what closes it. */
export const TABS: Tab[] = [
  {
    key: 'brand',
    label: 'Brand',
    region: 'page',
    roles: ['textHex', 'surfaceHex', 'primaryHex', 'secondaryHex', 'accentHex'],
    elements: [],
    logos: true
  },
  { key: 'page', label: 'Page', region: 'body-page', roles: [], elements: [], margins: true },
  {
    key: 'header',
    label: 'Header',
    region: 'header',
    roles: [],
    elements: [ELEMENTS.runningHeader],
    slots: 'header'
  },
  {
    key: 'headings',
    label: 'Headings',
    region: 'heading',
    roles: [],
    elements: [ELEMENTS.heading1, ELEMENTS.heading2]
  },
  { key: 'body', label: 'Body', region: 'body', roles: [], elements: [ELEMENTS.body] },
  {
    key: 'table',
    label: 'Tables',
    region: 'table',
    roles: [],
    elements: [ELEMENTS.tableHeader, ELEMENTS.tableCell, ELEMENTS.tableStripe]
  },
  { key: 'chart', label: 'Charts', region: 'chart', roles: [], elements: [ELEMENTS.chartLabel] },
  {
    key: 'footer',
    label: 'Footer',
    region: 'footer',
    roles: [],
    elements: [ELEMENTS.runningFooter],
    slots: 'footer'
  },
  { key: 'voice', label: 'Voice', region: 'body', roles: [], elements: [], voice: true }
];
